import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useAccountMovementStore } from "../../../store/accountMovementStore";
import { FileText, Printer, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";

interface AccountStatementDialogProps {
    isOpen: boolean;
    onClose: () => void;
    account: { id: number; name: string } | null;
}

export const AccountStatementDialog: React.FC<AccountStatementDialogProps> = ({ isOpen, onClose, account }) => {
    const [movements, setMovements] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { fetchDataDynamic } = useAccountMovementStore();

    // 1. loadStatement'ı useCallback ile sarmaladık
    const loadStatement = useCallback(async () => {
        if (!account?.id) return;
        setLoading(true);
        try {
            const res = await fetchDataDynamic({
                pageIndex: 1,
                pageSize: 1000,
                filter: {
                    logic: "and",
                    filters: [{ field: "accountId", operator: "eq", value: account.id }]
                },
                sort: [{ field: "date", dir: "asc" }]
            });
            setMovements(res.items || []);
        } catch (err) {
            console.error("Ekstre yükleme hatası:", err);
        } finally {
            setLoading(false);
        }
    }, [account?.id, fetchDataDynamic]);

    useEffect(() => {
        if (isOpen && account?.id) {
            loadStatement();
        } else if (!isOpen) {
            setMovements([]); // Dialog kapanınca veriyi temizle
        }
    }, [isOpen, account?.id, loadStatement]);

    const statementData = useMemo(() => {
        let runningBalance = 0;
        return movements.map((m, index) => {
            const change = m.direction === 'IN' ? m.amount : -m.amount;
            runningBalance += change;
            return {
                ...m,
                uniqueId: `${m.id}-${index}`, // DataTable için benzersiz key
                debit: m.direction === 'OUT' ? m.amount : 0,
                credit: m.direction === 'IN' ? m.amount : 0,
                balance: runningBalance
            };
        });
    }, [movements]);

    // Memory leak önlemek için formatlayıcıyı memoize edebiliriz veya basit tutabiliriz
    const formatCurrency = (value: number, currency: string = 'TRY') => {
        const validCurrency = currency === 'TL' ? 'TRY' : currency;
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: validCurrency }).format(value || 0);
    };

    // Header içeriğini bir değişkene alalım
    const headerElement = (
        <div className="flex justify-between items-center w-full pr-8">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                    <FileText size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold">{account?.name || 'Yükleniyor...'} - Cari Ekstre</h2>
                    <p className="text-xs text-gray-500 font-medium tracking-tight">Tüm hesap hareket dökümü</p>
                </div>
            </div>
            <div className="flex gap-2">
                <IconTextButton icon={Printer} className="btn text-sm">Yazdır</IconTextButton>
            </div>
        </div>
    );

    return (
        <Dialog
            header={headerElement}
            visible={isOpen}
            onHide={onClose}
            maximizable
            className="w-[90vw] md:w-[75vw]"
            resizable={false}
        >
            <div className="flex flex-col gap-4 p-1">
                <DataTable
                    value={statementData}
                    loading={loading}
                    className="p-datatable-sm"
                    stripedRows
                    rowHover
                    dataKey="uniqueId" // Önemli: Her satırın benzersiz bir anahtarı olmalı
                    emptyMessage="Bu cariye ait hareket bulunamadı."
                >
                    <Column field="date" header="Tarih" body={(row) => new Date(row.date).toLocaleDateString('tr-TR')} style={{ width: '120px' }} />
                    
                    <Column field="type" header="İşlem Türü" body={(row) => (
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase">
                            {row.direction === 'IN' ? <ArrowDownLeft size={14} className="text-emerald-500" /> : <ArrowUpRight size={14} className="text-blue-500" />}
                            {row.type === 'INVOICE' ? 'Fatura' : 'Ödeme/Tahsilat'}
                        </div>
                    )} />

                    <Column field="reference" header="Belge No" body={(row) => <span className="font-mono text-xs">{row.reference || '-'}</span>} />
                    
                    <Column field="description" header="Açıklama" />

                    <Column field="currency" header="Döviz/Kur" body={(row) => (
                        <div className="flex flex-col text-[10px]">
                            <span className="font-bold text-slate-700">{row.currency}</span>
                            {row.currency !== 'TRY' && (
                                <span className="text-slate-500 italic">Kur: {row.exchangeRate}</span>
                            )}
                        </div>
                    )} style={{ width: '100px' }} />

                    <Column field="debit" header="Borç" align="right" body={(row) => (
                        <div className="flex flex-col items-end">
                            <span className="text-red-600 font-medium">{row.debit > 0 ? formatCurrency(row.debit, row.currency) : '-'}</span>
                            {row.currency !== 'TRY' && (
                                <span className="text-[9px] text-gray-400">TL Karş: {formatCurrency(row.debit * (row.exchangeRate || 1), 'TRY')}</span>
                            )}
                        </div>
                    )} />

                    <Column field="credit" header="Alacak" align="right" body={(row) => (
                        <div className="flex flex-col items-end">
                            <span className="text-emerald-600 font-medium">{row.credit > 0 ? formatCurrency(row.credit, row.currency) : '-'}</span>
                            {row.currency !== 'TRY' && (
                                <span className="text-[9px] text-gray-400">TL Karş: {formatCurrency(row.credit * (row.exchangeRate || 1), 'TRY')}</span>
                            )}
                        </div>
                    )} />

                    <Column field="balance" header="Bakiye" align="right" className="font-bold" body={(row) => (
                        <span className={row.balance < 0 ? "text-red-700" : "text-emerald-700"}>
                            {formatCurrency(row.balance, 'TRY')} 
                        </span>
                    )} />
                </DataTable>
            </div>
        </Dialog>
    );
};